import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthForm } from "@/features/auth/hooks/use-login-form";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

type AuthFormProps = React.ComponentProps<"div"> & {
  newUser: boolean;
};

export function AuthForm({ newUser, className, ...props }: AuthFormProps) {
  const {
    emailLoading,
    anyLoading,
    error,
    errorKind,
    handleEmailLogin,
    // handleGitHubLogin,
    // handleGoogleLogin,
    email,
    setEmail,
  } = useAuthForm(newUser);

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <form onSubmit={(e) => void handleEmailLogin(e)}>
        <FieldGroup>
          {/* Header */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">{newUser ? "Welcome to Kairo" : "Welcome back!"}</h1>
            <p className="text-muted-foreground text-balance">
              {newUser ? "Sign up to your Kairo account" : "Log in to your Kairo account"}
            </p>
          </div>

          {/* Email field */}
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={anyLoading}
            />
          </Field>

          {/* Alerts */}
          {(!!error || !!errorKind) && (
            <Alert
              variant="destructive"
              data-testid={errorKind === "invite_full" ? "signup-invite-full" : undefined}
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Submit button */}
          <Field>
            <Button type="submit" disabled={anyLoading} className="w-full">
              {emailLoading ? "Sending magic link..." : "Send magic link"}
            </Button>
            <FieldDescription className="text-center">
              We'll send you a secure login link via email.
            </FieldDescription>
          </Field>

          {/* Field separator */}
          <FieldSeparator className="*:data-[slot=field-separator-content]:bg-background">
            {/* Or continue with */}
          </FieldSeparator>

          {/* Social login buttons */}
          {/* <Field className="grid grid-cols-2 gap-4">
            <Button
              className="cursor-pointer"
              variant="outline"
              type="button"
              onClick={handleGitHubLogin}
              disabled={anyLoading}
            >
              <span className="sr-only">Login with GitHub</span> */}
          {/* icon kept as-is */}
          {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
              >
                <path
                  d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"
                  fill="currentColor"
                />
              </svg>
            </Button>

            <Button
              className="cursor-pointer"
              variant="outline"
              type="button"
              onClick={handleGoogleLogin}
              disabled={anyLoading}
            >
              <span className="sr-only">Login with Google</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
              >
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
            </Button>
          </Field> */}
        </FieldGroup>
      </form>

      <FieldDescription className="text-muted-foreground px-6 text-center text-xs">
        By continuing, you agree to Kairo's{" "}
        <Link to="/terms" className="underline underline-offset-2">
          Terms of Use
        </Link>{" "}
        and acknowledge the{" "}
        <Link to="/privacy" className="underline underline-offset-2">
          Privacy Policy
        </Link>
        .
      </FieldDescription>
    </div>
  );
}
